import { AbstractControl } from '@angular/forms';
import { FormlyFieldConfig } from '@ngx-formly/core';

import { isCanadianIdentityCountry } from '@shared/constants/identity-formats';

import { luhnValidator } from './luhn';
import { siblingCountryCode } from './sibling-country';

const businessTaxIDPrefixes = [
  '10',
  '12',
  '60',
  '67',
  '50',
  '53',
  '01',
  '02',
  '03',
  '04',
  '05',
  '06',
  '11',
  '13',
  '14',
  '16',
  '21',
  '22',
  '23',
  '25',
  '34',
  '51',
  '52',
  '54',
  '55',
  '56',
  '57',
  '58',
  '59',
  '65',
  '30',
  '32',
  '35',
  '36',
  '37',
  '38',
  '61',
  '15',
  '24',
  '40',
  '44',
  '94',
  '95',
  '80',
  '90',
  '33',
  '39',
  '41',
  '42',
  '43',
  '46',
  '48',
  '62',
  '63',
  '64',
  '66',
  '68',
  '71',
  '72',
  '73',
  '74',
  '75',
  '76',
  '77',
  '85',
  '86',
  '87',
  '88',
  '91',
  '92',
  '93',
  '98',
  '99',
  '20',
  '26',
  '27',
  '45',
  '46',
  '47',
  '81',
  '82',
  '83',
  '84',
  '85',
  '86',
  '87',
  '88',
  '92',
  '31',
];

/**
 * The US EIN prefix allowlist. Applies to US tax ids ONLY.
 *
 * Kept as the default export shape so existing US-only call sites (e.g. the Linx template builder) are unchanged.
 * A Canadian Business Number has no prefix concept and would be rejected by every entry here, so any form that can
 * select a country must use `businessTaxIdValidatorFor` instead.
 */
export const businessTaxIDValidator = (control: AbstractControl) => {
  if (!control.value || control.value.length < 2) {
    return null;
  }

  const validId = businessTaxIDPrefixes.includes(control.value.slice(0, 2));

  return validId ? null : { pattern: { message: 'Please enter valid tax id.' } };
};

/**
 * Country-aware business tax id validation.
 *
 * US keeps the EIN prefix allowlist; Canada gets the Luhn check instead. The country is resolved per validation run
 * rather than captured once, because the user can change the country select after the field has rendered.
 */
export const businessTaxIdValidatorFor =
  (countryCodeKey: string) =>
  (control: AbstractControl, field?: FormlyFieldConfig): ReturnType<typeof businessTaxIDValidator> => {
    if (isCanadianIdentityCountry(siblingCountryCode(control, field, countryCodeKey))) {
      return luhnValidator(control);
    }

    return businessTaxIDValidator(control);
  };
